import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Sidebar.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Sidebar({ projectKey }) {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [expanded, setExpanded] = useState({
    requirements: false,
    testcases: false,
    testsuites: false,
    archived: false,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [requirementFolders, setRequirementFolders] = useState([]);
  const [testcaseFolders, setTestcaseFolders] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [archivedData, setArchivedData] = useState({
    folders: [],
    testCases: [],
    requirements: [],
    testSuites: [],
  });
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [newName, setNewName] = useState('');

  // Convert projectKey to projectId
  useEffect(() => {
    if (projectKey) {
      fetchProjectId();
    }
  }, [projectKey]);

  const fetchProjectId = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const project = data.data || data;
        if (project) {
          setProjectId(project.id || project._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  useEffect(() => {
    if (projectId && expanded.requirements) {
      fetchRequirementFolders();
    }
  }, [projectId, expanded.requirements]);

  useEffect(() => {
    if (projectId && expanded.testcases) {
      fetchTestcaseFolders();
    }
  }, [projectId, expanded.testcases]);

  useEffect(() => {
    if (projectId && expanded.testsuites) {
      fetchTestSuites();
    }
  }, [projectId, expanded.testsuites]);

  useEffect(() => {
    if (projectId && expanded.archived) {
      fetchArchivedItems();
    }
  }, [projectId, expanded.archived]);

  // Listen for sidebar refresh events
  useEffect(() => {
    const handleRefresh = (event) => {
      const { type } = event.detail || {};
      
      if (type === 'requirementFolders' && projectId) {
        fetchRequirementFolders();
      } else if (type === 'testcaseFolders' && projectId) {
        fetchTestcaseFolders();
      } else if (type === 'testSuites' && projectId) {
        fetchTestSuites();
      } else if (type === 'archived' && projectId) {
        fetchArchivedItems();
      } else if (type === 'all' && projectId) {
        fetchRequirementFolders();
        fetchTestcaseFolders();
        fetchTestSuites();
        fetchArchivedItems();
      }
    };

    window.addEventListener('refreshSidebar', handleRefresh);
    return () => window.removeEventListener('refreshSidebar', handleRefresh);
  }, [projectId]);

  const fetchRequirementFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/folders?projectId=${projectId}&type=requirement`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRequirementFolders(data.data || []);
      }
    } catch (err) {
      console.error('Error loading requirement folders:', err);
    }
  };

  const fetchTestcaseFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/folders?projectId=${projectId}&type=testcase`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTestcaseFolders(data.data || []);
      }
    } catch (err) {
      console.error('Error loading testcase folders:', err);
    }
  };

  const fetchTestSuites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/suites?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTestSuites(data.data || []);
      }
    } catch (err) {
      console.error('Error loading test suites:', err);
    }
  };

  const fetchArchivedItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/archive/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setArchivedData(data.data || {
          folders: [],
          testCases: [],
          requirements: [],
          testSuites: [],
        });
      }
    } catch (err) {
      console.error('Error loading archived items:', err);
    }
  };

  const toggleSection = section => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const closeOnNavigate = (path) => {
    navigate(path);
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  const handleUnarchive = async (item, type) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/archive/restore/${type}/${item.id || item._id}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 409) {
        // Duplicate detected, show rename modal
        const data = await response.json();
        setRenameItem({ ...item, type, endpoint });
        setNewName(item.name || item.title || '');
        setShowRenameModal(true);
      } else if (response.ok) {
        alert(`${type.slice(0, -1)} restored successfully!`);
        fetchArchivedItems(); // Refresh archived items
        // Refresh the appropriate list
        if (type === 'folders') {
          if (item.type === 'requirement') {
            fetchRequirementFolders();
          } else {
            fetchTestcaseFolders();
          }
        } else if (type === 'testcases') {
          // Trigger refresh if needed
        } else if (type === 'requirements') {
          // Trigger refresh if needed
        } else if (type === 'suites') {
          fetchTestSuites();
        }
      } else {
        const data = await response.json();
        alert(`Failed to restore: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error unarchiving item:', err);
      alert('Failed to restore item');
    }
  };

  const handleRenameAndRestore = async () => {
    if (!newName.trim()) {
      alert('Please enter a new name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const body = renameItem.type === 'folders' || renameItem.type === 'suites'
        ? { newName: newName.trim() }
        : { newTitle: newName.trim() };

      const response = await fetch(renameItem.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        alert(`${renameItem.type.slice(0, -1)} restored successfully with new name!`);
        setShowRenameModal(false);
        setRenameItem(null);
        setNewName('');
        fetchArchivedItems(); // Refresh archived items
        // Refresh the appropriate list
        if (renameItem.type === 'folders') {
          if (renameItem.type === 'requirement') {
            fetchRequirementFolders();
          } else {
            fetchTestcaseFolders();
          }
        } else if (renameItem.type === 'suites') {
          fetchTestSuites();
        }
      } else {
        const data = await response.json();
        alert(`Failed to restore: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error renaming and restoring item:', err);
      alert('Failed to restore item');
    }
  };

  const renderFolderTree = (folders, parentId = null, type = 'requirement', isArchived = false) => {
    const children = folders.filter(f => {
      const fParentId = f.parentId?.id || f.parentId?._id || f.parentId;
      return parentId === null ? !fParentId : fParentId === parentId;
    });

    return children.map(folder => {
      const folderId = folder.id || folder._id;
      return (
        <div key={folderId} className="folder-item">
          <div className="folder-link" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div
              style={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() =>
                !isArchived && navigate(
                  type === 'requirement'
                    ? `/projects/${projectKey.toLowerCase()}/requirements?folderId=${folderId}`
                    : `/projects/${projectKey.toLowerCase()}/testcases?folderId=${folderId}`
                )
              }
            >
              <span className="folder-icon">📁</span>
              <span className="folder-name">{folder.name}</span>
            </div>
            {isArchived && (
              <button
                className="btn btn-sm btn-outline-light"
                style={{ fontSize: '10px', padding: '2px 6px', marginLeft: '5px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnarchive(folder, 'folders');
                }}
                title="Restore folder"
              >
                ↩
              </button>
            )}
          </div>
          {renderFolderTree(folders, folderId, type, isArchived)}
        </div>
      );
    });
  };

  if (!projectId) {
    return (
      <>
        <button className="sidebar-toggle" onClick={() => setIsOpen(prev => !prev)} aria-label="Toggle sidebar">☰</button>
        <div className={`sidebar ${isOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h5>Navigation</h5>
          </div>
          <div className="sidebar-menu">
            <Link to="/projects" className="sidebar-item" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
              <span className="sidebar-icon">🏗️</span>
              Projects
            </Link>
          </div>
        </div>
        {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <button className="sidebar-toggle" onClick={() => setIsOpen(prev => !prev)} aria-label="Toggle sidebar">☰</button>
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/projects" className="back-link" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
            ← Projects
          </Link>
        </div>

      <div className="sidebar-menu">
        {/* Dashboard */}
        <div className="sidebar-section">
          <div
            className="sidebar-item"
            onClick={() => {
              navigate(`/projects/${projectKey.toLowerCase()}/dashboard`);
              if (window.innerWidth <= 768) setIsOpen(false);
            }}
          >
            <span className="expand-icon" style={{ visibility: 'hidden' }}>▶</span>
            <span className="sidebar-icon">📊</span>
            <span>Dashboard</span>
          </div>
        </div>

        {/* Requirements Section */}
        <div className="sidebar-section">
          <div
            className={`sidebar-item ${expanded.requirements ? 'expanded' : ''}`}
            onClick={() => {
              toggleSection('requirements');
              navigate(`/projects/${projectKey.toLowerCase()}/requirements`);
            }}
          >
            <span className="expand-icon">{expanded.requirements ? '▼' : '▶'}</span>
            <span className="sidebar-icon">📋</span>
            <span>Requirements</span>
          </div>
          {expanded.requirements && (
            <div className="sidebar-submenu">
              <div
                className="sidebar-subitem new-folder"
                onClick={() => navigate(`/projects/${projectKey.toLowerCase()}/folders/requirement`)}
              >
                <span className="sidebar-icon">➕</span>
                Manage Folders
              </div>
              {requirementFolders.length > 0 ? (
                <div className="folder-tree">{renderFolderTree(requirementFolders, null, 'requirement')}</div>
              ) : (
                <div className="sidebar-subitem empty">No folders</div>
              )}
            </div>
          )}
        </div>

        {/* Test Cases Section */}
        <div className="sidebar-section">
          <div
            className={`sidebar-item ${expanded.testcases ? 'expanded' : ''}`}
            onClick={() => {
              toggleSection('testcases');
              navigate(`/projects/${projectKey.toLowerCase()}/testcases`);
            }}
          >
            <span className="expand-icon">{expanded.testcases ? '▼' : '▶'}</span>
            <span className="sidebar-icon">✅</span>
            <span>Test Cases</span>
          </div>
          {expanded.testcases && (
            <div className="sidebar-submenu">
              <div
                className="sidebar-subitem new-folder"
                onClick={() => navigate(`/projects/${projectKey.toLowerCase()}/folders/testcase`)}
              >
                <span className="sidebar-icon">➕</span>
                Manage Folders
              </div>
              {testcaseFolders.length > 0 ? (
                <div className="folder-tree">{renderFolderTree(testcaseFolders, null, 'testcase')}</div>
              ) : (
                <div className="sidebar-subitem empty">No folders</div>
              )}
              {/* Unassigned: TCs with no folder */}
              <div
                className="sidebar-subitem"
                style={{ cursor: 'pointer', paddingLeft: '8px', fontSize: '0.85rem', color: '#9ca3af' }}
                onClick={() => navigate(`/projects/${projectKey.toLowerCase()}/testcases?folderId=none`)}
              >
                <span style={{ marginRight: 5 }}>📂</span> Unassigned
              </div>
            </div>
          )}
        </div>

        {/* Test Suites Section */}
        <div className="sidebar-section">
          <div
            className={`sidebar-item ${expanded.testsuites ? 'expanded' : ''}`}
            onClick={() => {
              toggleSection('testsuites');
              navigate(`/projects/${projectKey.toLowerCase()}/test-suites`);
            }}
          >
            <span className="expand-icon">{expanded.testsuites ? '▼' : '▶'}</span>
            <span className="sidebar-icon">📦</span>
            <span>Test Suites</span>
          </div>
          {expanded.testsuites && (
            <div className="sidebar-submenu">
              {testSuites.length === 0 ? (
                <div className="sidebar-subitem empty">No test suites</div>
              ) : (
                testSuites.map(suite => {
                  const id = suite._id || suite.id;
                  return (
                    <div
                      key={id}
                      className="sidebar-subitem"
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => navigate(`/projects/${projectKey.toLowerCase()}/testcases?suiteId=${id}`)}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ marginRight: 5 }}>📦</span>{suite.name}
                      </span>
                      {suite.testCaseCount != null && (
                        <span style={{ fontSize: '0.7rem', background: '#e9ecef', borderRadius: 10, padding: '1px 7px', flexShrink: 0, marginLeft: 4 }}>
                          {suite.testCaseCount}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Test Runs Section */}
        <div className="sidebar-section">
          <div
            className="sidebar-item"
            onClick={() => {
              navigate(`/projects/${projectKey.toLowerCase()}/runs`);
            }}
          >
            <span className="sidebar-icon">🚀</span>
            <span>Test Runs</span>
          </div>
        </div>

        <div className="sidebar-section">
          <div
            className="sidebar-item"
            onClick={() => {
              navigate(`/projects/${projectKey.toLowerCase()}/results`);
            }}
          >
            <span className="sidebar-icon">🧪</span>
            <span>Test Results</span>
          </div>
        </div>

        {/* Archived Items Section */}
        <div className="sidebar-section">
          <div
            className={`sidebar-item ${expanded.archived ? 'expanded' : ''}`}
            onClick={() => toggleSection('archived')}
          >
            <span className="expand-icon">{expanded.archived ? '▼' : '▶'}</span>
            <span className="sidebar-icon">🗄️</span>
            <span>Archived Items</span>
          </div>
          {expanded.archived && (
            <div className="sidebar-submenu">
              <div
                className="sidebar-subitem new-folder"
                onClick={() => navigate(`/projects/${projectKey.toLowerCase()}/archived`)}
              >
                <span className="sidebar-icon">📋</span>
                View All Archived Items
              </div>
              {/* Archived Folders */}
              {archivedData.folders && archivedData.folders.length > 0 && (
                <div className="archived-category">
                  <div className="archived-category-title">Folders</div>
                  {renderFolderTree(
                    archivedData.folders.filter(f => f.type === 'requirement'),
                    null,
                    'requirement',
                    true
                  )}
                  {renderFolderTree(
                    archivedData.folders.filter(f => f.type === 'testcase'),
                    null,
                    'testcase',
                    true
                  )}
                </div>
              )}
              
              {/* Archived Requirements */}
              {archivedData.requirements && archivedData.requirements.length > 0 && (
                <div className="archived-category">
                  <div className="archived-category-title">Requirements ({archivedData.requirements.length})</div>
                  {archivedData.requirements.slice(0, 5).map(req => (
                    <div
                      key={req.id || req._id}
                      className="sidebar-subitem archived-item"
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <span className="sidebar-icon">📄</span>
                        {req.title}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-light"
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnarchive(req, 'requirements');
                        }}
                        title="Restore requirement"
                      >
                        ↩
                      </button>
                    </div>
                  ))}
                  {archivedData.requirements.length > 5 && (
                    <div className="sidebar-subitem empty">
                      +{archivedData.requirements.length - 5} more...
                    </div>
                  )}
                </div>
              )}

              {/* Archived Test Cases */}
              {archivedData.testCases && archivedData.testCases.length > 0 && (
                <div className="archived-category">
                  <div className="archived-category-title">Test Cases ({archivedData.testCases.length})</div>
                  {archivedData.testCases.slice(0, 5).map(tc => (
                    <div
                      key={tc.id || tc._id}
                      className="sidebar-subitem archived-item"
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <span className="sidebar-icon">✓</span>
                        {tc.title}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-light"
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnarchive(tc, 'testcases');
                        }}
                        title="Restore test case"
                      >
                        ↩
                      </button>
                    </div>
                  ))}
                  {archivedData.testCases.length > 5 && (
                    <div className="sidebar-subitem empty">
                      +{archivedData.testCases.length - 5} more...
                    </div>
                  )}
                </div>
              )}

              {/* Archived Test Suites */}
              {archivedData.testSuites && archivedData.testSuites.length > 0 && (
                <div className="archived-category">
                  <div className="archived-category-title">Test Suites ({archivedData.testSuites.length})</div>
                  {archivedData.testSuites.map(suite => (
                    <div
                      key={suite.id || suite._id}
                      className="sidebar-subitem archived-item"
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <span className="sidebar-icon">📦</span>
                        {suite.name}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-light"
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnarchive(suite, 'suites');
                        }}
                        title="Restore test suite"
                      >
                        ↩
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* No Archived Items */}
              {(!archivedData.folders || archivedData.folders.length === 0) &&
               (!archivedData.requirements || archivedData.requirements.length === 0) &&
               (!archivedData.testCases || archivedData.testCases.length === 0) &&
               (!archivedData.testSuites || archivedData.testSuites.length === 0) && (
                <div className="sidebar-subitem empty">No archived items</div>
              )}
            </div>
          )}
        </div>

        {/* Other Sections */}
        <Link to={`/jenkins?projectId=${projectId}`} className="sidebar-item" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
          <span className="sidebar-icon">⚙️</span>
          Jenkins
        </Link>

        <Link to={`/jira?projectId=${projectId}`} className="sidebar-item" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
          <span className="sidebar-icon">🔗</span>
          JIRA
        </Link>

        <Link to={`/projects/${projectKey.toLowerCase()}/settings?tab=jira-import`} className="sidebar-item" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
          <span className="sidebar-icon">⬇️</span>
          JIRA Import
        </Link>

        <Link to={`/projects/${projectId}/settings`} className="sidebar-item" onClick={() => { if (window.innerWidth <= 768) setIsOpen(false); }}>
          <span className="sidebar-icon">🔧</span>
          Project Settings
        </Link>
      </div>

      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      {/* Rename Modal for Duplicate Detection */}
      {showRenameModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Duplicate Name Detected</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenameItem(null);
                    setNewName('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  A {renameItem?.type.slice(0, -1)} with the name "<strong>{renameItem?.name || renameItem?.title}</strong>" already exists.
                </p>
                <p>Please enter a new name to restore this item:</p>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter new name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameAndRestore();
                    }
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenameItem(null);
                    setNewName('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRenameAndRestore}
                  disabled={!newName.trim()}
                >
                  Restore with New Name
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default Sidebar;
